import { useContext, useEffect, useReducer, useRef, useState } from "react";

import FilterModal from "./FilterModal";
import Sort from "../Sort/Sort";
import FilterDropdown from "./FilterDropdown";
import { getFilterConfig } from "../../services/appConfig";
import { AzureFilter } from "../../types/AlgoliaTypes";
import { useLocation } from "react-router-dom";
import { useCurrentRefinements } from "react-instantsearch";
import { AzureConfigContext } from "../../contexts/AzureConfigContext";

interface BasicFilterStructure {
    filterName: string
    filterAttr: string
    order: number | undefined;
    limit?: number
    pinnedItems?: string[] | undefined
    sortItems?: string[] | undefined
    sortItemCriteria?: string[] | undefined
}

export interface FiltersStruct { 
    filterName: string
    filterAttr: string
    limit?: number
    searchable?: boolean
    sort?: string[]
    nested: boolean;
    nestedAttrs?: BasicFilterStructure[] 
    pinnedItems?: string[] | undefined
    sortItems?: string[] | undefined
    sortItemCriteria?: string[] | undefined
    placeholderText?: string
    order: number | undefined;
}

interface BaseFilterProps {
    id: number;
    filterLabel: string;
    filters:  FiltersStruct[]
}

enum FilterActionType {
    TOGGLE_FILTER = 'toggleFilterDropdown',
    SET_FILTERS_STATE = 'setFilters'
}

type FilterAction = 
    { type: FilterActionType.TOGGLE_FILTER, payload: { id: number } } |
    { type: FilterActionType.SET_FILTERS_STATE, payload: { filters: FilterState[] } }


export interface FilterState extends BaseFilterProps {
    isOpen: boolean;
}

  
const filterButtonToIdMap: { [key: string]: number } = {
  "Material": 0,
  "Color": 1,
  "Price": 2,
  "More Filters": 3
};

function filtersReducer(state: FilterState[], action: FilterAction): FilterState[] {
    const { type, payload } = action;

    switch(type) {
        case FilterActionType.TOGGLE_FILTER:
            return state.map((filter) => 
                filter.id === action.payload.id ? { ...filter, isOpen: !filter.isOpen } : filter
            );
        case FilterActionType.SET_FILTERS_STATE:
            return payload.filters;
        default:
            return state;
    }

}

export default function Filters() {
    const [initialFilterState, setInitialFilterState] = useState<FilterState[]>([]);
    const [isMbModalOpen, setIsMbModalOpen] = useState(false);
    const [state, dispatch] = useReducer(filtersReducer, initialFilterState)
    const location = useLocation();
    const azureFilterConfig = useContext(AzureConfigContext);
    const currentRefinements = useCurrentRefinements();

    const dropdownRef = useRef<Map<number, HTMLDivElement | null> | null>(null);

    function handleToggleDropdown(id: number): void {
        dispatch(
            {
                type: FilterActionType.TOGGLE_FILTER,
                payload: { id }
            }
        );
    }

    const handleClickOutside = (event: MouseEvent) => {
        const map = getMap();
        state.map(filter => {
            const node = map.get(filter.id)
            if(filter.isOpen) {
                if(node && !node?.contains(event.target as Node)) {
                    handleToggleDropdown(filter.id);
                }
            }
            
        })
    }

    function getMap() {
        if (!dropdownRef.current) {
          dropdownRef.current = new Map();
        }
        return dropdownRef.current;
    } 

    function newDropdownRef(node: HTMLDivElement | null, id: number): void {
        const map = getMap();
        if (node) {
            map.set(id, node);
        } else {
            map.delete(id);
        }
    }

    function countRefinedItems(filters: FiltersStruct[]) {
        let count = 0;
        filters.forEach(item => {
            currentRefinements.items.forEach(refinedItem => {
                if(item.filterAttr === refinedItem.attribute){
                    count += refinedItem.refinements.length
                }
                
            })
        })
        return count;
    }

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        
        return () => {
            document.removeEventListener('click', handleClickOutside);
        }
    }, [state])

    useEffect(() => {
        const getConfig = async () => {

            if(azureFilterConfig){
                let filterState: FilterState[] = []
                azureFilterConfig.forEach((filterItem, idx) => {
                    const filterButton = filterItem.filterButton;
                    const filterId = filterButtonToIdMap[filterButton];
    
    
                    const pathFamily = location.pathname.split("/")[2];
                    
                    let shouldIncludeFilter =  
                        filterId !== undefined &&
                        (filterItem.family?.some(f => f?.family.toUpperCase() == pathFamily?.toUpperCase()) || // Check family
                        (filterItem.display && 
                        filterItem.display?.some(d => d?.family.toUpperCase() == pathFamily?.toUpperCase()))); // Check display
                    
                    if(location.pathname.split("/")[1] === "products") {
                        shouldIncludeFilter = filterId !== undefined &&
                        (filterItem.family?.some(f => f?.family.toUpperCase() == "PRODUCTS") || // Check family
                        (filterItem.display && 
                        filterItem.display?.some(d => d?.family.toUpperCase() == "PRODUCTS"))); // Check display
                    }

                    if(filterId !== undefined && shouldIncludeFilter) {
                        let existingFilter = filterState.find(item => item.id === filterId);
    
                        let order;
                        if (filterItem.family) {
                            const familyItem = filterItem.family.find(f => f.family.toUpperCase() === pathFamily.toUpperCase());
                            order = familyItem ? familyItem.order : undefined;
                        } else if (filterItem.display) {
                            const displayItem = filterItem.display.find(d => d.family.toUpperCase() === pathFamily.toUpperCase());
                            order = displayItem ? displayItem.order : undefined;
                        }
                        
                        let existingFilterItem;

                        if(filterItem.nested) {
                            existingFilterItem = existingFilter?.filters.find(item => item.filterName === filterItem.filterHeader)
                            
                            const filterStructure: BasicFilterStructure = {
                                filterName: filterItem.filterSubHeader,
                                filterAttr: `filters.${filterItem.algoliaPropertyName}`,
                                order: order !== undefined ? parseInt(order) : undefined,
                                limit: filterItem.limit ?? 10,
                                pinnedItems: filterItem.pinnedItems,
                                sortItems: filterItem.sortItems,
                                sortItemCriteria: filterItem.sortItemCriteria
                            }

                            if(existingFilterItem && existingFilterItem.nestedAttrs) {
                                existingFilterItem?.nestedAttrs.push(filterStructure)
                            }
                        }

                        let filterName = filterItem.filterHeader;
                        
                        if(filterItem.translateValue) {
                            if(filterItem.nestedObject) {
                                filterName = window.steelcase_i18n["steelcase-finish-library"][filterItem.nestedObject][filterItem.translateValue]
                            } else {
                                filterName = window.steelcase_i18n["steelcase-finish-library"][filterItem.translateValue]
                            }
                        }

                        if(filterName === "More Filters" && window.steelcase_i18n["steelcase-finish-library"].more_filters) {
                            filterName = window.steelcase_i18n["steelcase-finish-library"].more_filters
                            if(window.steelcase_i18n["steelcase-finish-library"].more_filters === "More Filterss") {
                                filterName = "More Filters"
                            }
                        }
                        
                        let filterButtonText = filterButton;

                        if(filterButton === "Material" && window.steelcase_i18n["steelcase-finish-library"].material_title) {
                            filterButtonText = window.steelcase_i18n["steelcase-finish-library"].material_title
                        }
                        if(filterButton === "Color" && window.steelcase_i18n["steelcase-finish-library"].color_title) {
                            filterButtonText = window.steelcase_i18n["steelcase-finish-library"].color_title
                        }
                        if(filterButton === "Price" && window.steelcase_i18n["steelcase-finish-library"].price) {
                            filterButtonText = window.steelcase_i18n["steelcase-finish-library"].price 
                        }
                        if(filterButton === "More Filters" && window.steelcase_i18n["steelcase-finish-library"].more_filters) {
                            filterButtonText = window.steelcase_i18n["steelcase-finish-library"].more_filters;
                        }
                        
                        // Added because of wrong translation
                        if(filterButtonText == "More filterss") {
                            filterButtonText = "More Filters"
                        }

                        if(filterName == "More filterss") {
                            filterName = "More Filters"
                        }

                        if (!existingFilter) { 
                            existingFilter = {
                              id: filterId,
                              filterLabel: filterButtonText,
                              filters: [],
                              isOpen: false
                            };
                            filterState.push(existingFilter)
                        }
                        
                        if(filterItem.nested && existingFilterItem === undefined) {
                            existingFilter.filters.push({
                                filterName: filterName,
                                filterAttr: "",
                                searchable: filterItem.searchable,
                                placeholderText: filterItem.placeholderText,
                                
                                nested: true,
                                nestedAttrs: [
                                    {
                                        filterName: filterItem.filterSubHeader, 
                                        filterAttr: `filters.${filterItem.algoliaPropertyName}`, 
                                        order: order !== undefined ? parseInt(order) : undefined,
                                        limit: filterItem.limit ?? 10,
                                        pinnedItems: filterItem.pinnedItems,
                                        sortItems: filterItem.sortItems,
                                        sortItemCriteria: filterItem.sortItemCriteria
                                    }
                                ],
                                sort: filterItem.sort ? filterItem.sort : ["isRefined", "count:desc", "name:asc"],
                                pinnedItems: filterItem.pinnedItems,
                                sortItems: filterItem.sortItems,
                                sortItemCriteria: filterItem.sortItemCriteria,
                                order: undefined
                            })
                        } 
                        if(!filterItem.nested) {
                            existingFilter.filters.push({
                                filterName: filterName, 
                                filterAttr: `filters.${filterItem.algoliaPropertyName}`,
                                searchable: filterItem.searchable,
                                placeholderText: filterItem.placeholderText,
                                limit: filterItem.limit ?? 10,
                                sort: filterItem.sort ? filterItem.sort : ["isRefined", "count:desc", "name:asc"],
                                pinnedItems: filterItem.pinnedItems,
                                sortItems: filterItem.sortItems,
                                sortItemCriteria: filterItem.sortItemCriteria,
                                order: order !== undefined ? parseInt(order) : undefined,
                                nested: false
                            });
                        }
                    }
                })

                filterState.forEach(existingFilter => {
                    existingFilter.filters.sort((a, b) => {
                        if (a.order === undefined && b.order === undefined) {
                            return 0;
                        } else if (a.order === undefined) {
                            return 1;
                        } else if (b.order === undefined) {
                            return -1;
                        } else {
                            return a.order - b.order;
                        }
                    });
                });
                
                setInitialFilterState(filterState)
                dispatch({ 
                    type: FilterActionType.SET_FILTERS_STATE,
                    payload: { filters: filterState } 
                }); 
            }
        };
        getConfig();
    }, [azureFilterConfig])

    return (
        <>
            <div className={"finish-filters finish-filters__container-flex"} style={{ position: "relative" }}>
                {/* Desktop Filter Buttons */}
                <div className={"finish-filters--flex hide-mobile--flex"} >
                    {
                        state.map((item, idx) => {
                            let currentRefinedAttrs = countRefinedItems(item.filters);
                            return (
                                    <div key={idx} className={"finish-filters__button--wrapper"}>
                                        <button className={"finish-filters__button"} onClick={(event) => {
                                            event.stopPropagation();
                                            event.preventDefault();
                                            handleToggleDropdown(item.id)  
                                        }}>
                                            {item.filterLabel} {currentRefinedAttrs > 0 && <span className={"finish-filters-modal__results-count"}>{currentRefinedAttrs}</span>}
                                        </button>
                                    </div>
                            )
                        })
                    }
                </div>
                {/* Mobile Filter Button */}
                <div className={"finish-filters__button--wrapper show-mobile"}>
                        <button 
                            className={"finish-filters__button finish-filters__button--full-width"}
                            onClick={() => setIsMbModalOpen(true)}
                        >
                            {window.steelcase_i18n["steelcase-finish-library"].filters_title || "Filters"} 
                        </button>
                        <FilterModal 
                            handleClose={() => setIsMbModalOpen(false)} 
                            isOpen={isMbModalOpen} 
                            filters={
                                initialFilterState.flatMap((item) => 
                                    item.filters.map((filter) => {
                                            return filter
                                        }
                                    )
                                )
                            } 
                            isMobile={true}
                        />
                </div>
                {/* Desktop Filter Dropdown */}
                {
                    state.map((item, idx) => {
                        return (
                            <FilterDropdown key={idx} {...item} toggleDropdown={newDropdownRef} toggleDropdownHandler={handleToggleDropdown} isMobile={false} />
                        )
                    })
                }
                {/* Sort */}
                <Sort />
            </div>
        </>
    )
}